import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';
import { useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthProvider';
import { Unauthorized } from '../../components/Unauthorized/Unauthorized';
import { UsersPage } from '../../pages/UsersPage';
import { TenantsPage } from '../../pages/TenantsPage';
import { TerminalsPage } from '../../pages/TerminalsPage';
import { MonitoringPage } from '../../pages/MonitoringPage/MonitoringPage';
import { MaintenancePage } from '../../pages/MaintenancePage';
import { TenantMaintenancePage } from '../../pages/TenantMaintenancePage';
import { PosShellPage } from '../../pages/PosShellPage';
import { KdsPage } from '../../pages/KdsPage';
import { BackofficePage } from '../../pages/BackofficePage';
import { DashboardPage } from '../../pages/DashboardPage/public';
import { DashboardAccessDenied } from '../../pages/DashboardPage/components/DashboardAccessDenied/DashboardAccessDenied';
import { EmployeeSessionProvider } from '../../contexts/EmployeeSessionProvider';
import { AdminEventsProvider } from '../../contexts/AdminEventsProvider';
import { TenantMaintenanceProvider } from '../../contexts/TenantMaintenanceProvider';

export interface AuthenticatedRoutesProps {}

type TenantRouteAccess = {
  tenantId: string | undefined;
  isTenantAllowed: boolean;
};

// Architectural summary: Shared tenant-route guard logic for tenant-scoped authenticated pages.
const useTenantRouteAccess = (): TenantRouteAccess => {
  const { tenantId } = useParams();
  const { availableTenants, setSelectedTenantId } = useAuth();

  const isTenantAllowed = tenantId
    ? availableTenants.some((tenant) => tenant.tenantId === tenantId)
    : false;

  useEffect(() => {
    if (tenantId && isTenantAllowed) {
      setSelectedTenantId(tenantId);
    }
  }, [tenantId, isTenantAllowed, setSelectedTenantId]);

  return { tenantId, isTenantAllowed };
};

export const TenantMaintenanceRedirect: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { tenantId } = useParams();
  const { user, availableTenants } = useAuth();
  const isMaintained = user?.role !== 'super_admin' && tenantId
    ? availableTenants.some((tenant) => tenant.tenantId === tenantId && tenant.maintenanceMode === true)
    : false;

  return isMaintained ? <Navigate to={`/t/${encodeURIComponent(tenantId!)}/maintenance`} replace /> : children;
};

// Summary: Gates children so only super-admin users can reach admin-only routes.
export const SuperAdminOnly: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user } = useAuth();
  return user?.role === 'super_admin' ? children : <Navigate to="/home" replace />;
};

// Summary: Gates dashboard to admin/super-admin; other roles get a denied state, no polling.
export const AdminDashboardOnly: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user } = useAuth();
  const canAccessDashboard = user?.role === 'super_admin' || user?.role === 'admin';
  return canAccessDashboard ? children : <DashboardAccessDenied />;
};

// Summary: Resolves post-login landing to tenant-scoped backoffice home.
export const PosEntryRoute: React.FC = () => {
  const { user, selectedTenantId, availableTenants } = useAuth();

  const targetTenantId = selectedTenantId ?? availableTenants[0]?.tenantId;
  if (!targetTenantId) {
    if (user?.role === 'super_admin') {
      return <Navigate to="/tenants" replace />;
    }
    return <Unauthorized />;
  }

  return <Navigate to={`/t/${targetTenantId}/backoffice`} replace />;
};

// Summary: Validates tenant route access and persists active tenant context before rendering backoffice home.
export const TenantScopedBackoffice: React.FC = () => {
  const { tenantId, isTenantAllowed } = useTenantRouteAccess();

  if (!tenantId) {
    return <Unauthorized />;
  }

  if (!isTenantAllowed) {
    return <Unauthorized />;
  }

  return <BackofficePage tenantId={tenantId} />;
};

// Summary: Validates tenant route access and persists active tenant context before rendering POS shell.
export const TenantScopedPos: React.FC = () => {
  const { tenantId, isTenantAllowed } = useTenantRouteAccess();

  if (!tenantId) {
    return <Unauthorized />;
  }

  if (!isTenantAllowed) {
    return <Unauthorized />;
  }

  return <PosShellPage tenantId={tenantId} />;
};

// Architectural summary: Tenant-gated KDS route wrapper.
export const TenantScopedKds: React.FC = () => {
  const { tenantId, isTenantAllowed } = useTenantRouteAccess();

  if (!tenantId) {
    return <Unauthorized />;
  }

  if (!isTenantAllowed) {
    return <Unauthorized />;
  }

  return <KdsPage tenantId={tenantId} />;
};

// Summary: Validates tenant dashboard access and persists active tenant context before rendering dashboard.
export const TenantScopedDashboard: React.FC = () => {
  const { tenantId, isTenantAllowed } = useTenantRouteAccess();

  if (!tenantId) {
    return <Unauthorized />;
  }

  if (!isTenantAllowed) {
    return <Unauthorized />;
  }

  return (
    <AdminDashboardOnly>
      <DashboardPage />
    </AdminDashboardOnly>
  );
};

// Summary: Mounts authenticated router tree for backoffice, POS, and super-admin pages.
export const AuthenticatedRoutes: React.FC<AuthenticatedRoutesProps> = () => {
  const { isAuthenticated } = useAuth();

  return isAuthenticated ? (
    <EmployeeSessionProvider>
      <AdminEventsProvider>
        <BrowserRouter>
          <TenantMaintenanceProvider>
            <Routes>
              <Route path="/t/:tenantId/pos" element={<TenantMaintenanceRedirect><TenantScopedPos /></TenantMaintenanceRedirect>} />
              <Route path="/t/:tenantId/kds" element={<TenantMaintenanceRedirect><TenantScopedKds /></TenantMaintenanceRedirect>} />
              <Route path="/t/:tenantId/dashboard" element={<TenantMaintenanceRedirect><TenantScopedDashboard /></TenantMaintenanceRedirect>} />
              <Route path="/t/:tenantId/maintenance" element={<TenantMaintenancePage />} />
              <Route path="/home" element={<PosEntryRoute />} />
              <Route path="/pos" element={<PosEntryRoute />} />
              <Route element={<Layout />}>
                <Route path="/t/:tenantId/backoffice" element={<TenantMaintenanceRedirect><TenantScopedBackoffice /></TenantMaintenanceRedirect>} />
                <Route path="/" element={<Navigate to="/home" replace />} />
              <Route
                path="/users"
                element={
                  <SuperAdminOnly>
                    <UsersPage />
                  </SuperAdminOnly>
                }
              />
              <Route
                path="/tenants"
                element={
                  <SuperAdminOnly>
                    <TenantsPage />
                  </SuperAdminOnly>
                }
              />
              <Route
                path="/terminals"
                element={
                  <SuperAdminOnly>
                    <TerminalsPage />
                  </SuperAdminOnly>
                }
              />
              <Route
                path="/monitoring"
                element={
                  <SuperAdminOnly>
                    <MonitoringPage />
                  </SuperAdminOnly>
                }
              />
              <Route
                path="/maintenance"
                element={
                  <SuperAdminOnly>
                    <MaintenancePage />
                  </SuperAdminOnly>
                }
              />
              </Route>
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </TenantMaintenanceProvider>
        </BrowserRouter>
      </AdminEventsProvider>
    </EmployeeSessionProvider>
  ) : (
    <Unauthorized />
  );
};
